import { hasMeasurementConsent, isPartnershipBooking, resolveCampaignParameters } from '../../../../static/js/campaign-attribution.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  CONTACT_EMAIL, CORAL, DEFAULT_SLUG, DayStrip, INK, LINE, MUTED, NAVY, Notice,
  PrimaryButton, SectionHeading, Shell, SlotGrid, TimezoneChoice,
} from '../components/booking-ui';
import { SelectField } from '../components/select-field';
import { detectTimezone, formatTime, readsAsBrisbane } from '../lib/timezones';
import { groupByLocalDay } from '../lib/group-days';
import {
  brisbaneToday, useAvailability, useBook, useBookingType, type AvailabilitySlot,
} from '../hooks/use-public-booking';

/**
 * The public booking page. Replaces Calendly.
 *
 * It used to live in the FRIDAY app. FRIDAY is the internal business system, so a page a
 * prospect is asked to open had no business being served from it. The page moved here;
 * the API did not, and does not need to.
 *
 * MOBILE FIRST, and not as a slogan: the highest-value visitor to this page is a
 * master-class attendee scanning a QR code from their seat, on a phone, with the room
 * already standing up. One column, a thumb-scrollable day strip, 44px targets, and a
 * form short enough to finish before the person next to them starts packing up.
 *
 * Above 768px it becomes two columns, with the context and the form on the left and the
 * calendar on the right. The grid areas are in styles.css and the DOM order is the mobile
 * order, so the phone case is the one with no layout code in front of it at all.
 *
 * TIMES ARE NOT BRISBANE ANY MORE, or rather they are not ONLY Brisbane. Every slot
 * carries an instant and the page renders it in the visitor's own zone, names the zone,
 * and offers the other one. See lib/timezones.ts for why none of that can be arithmetic.
 */

/**
 * How long the times on screen are trusted before a returning visitor gets fresh ones.
 *
 * Well inside the 15 minutes a slot token lives, so a token is refreshed before it can
 * expire, and long enough that flicking to another tab and back does not refetch.
 */
const STALE_AFTER_MS = 3 * 60 * 1000;

/** Matches the API's own check, so the page refuses what the API would refuse. */
const EMAIL_RE = /^[\w.+'-]+@[\w-]+(?:\.[\w-]+)+$/;

/** The DOM id `Field` derives from its label, so a submit can focus what it is talking about. */
function fieldId(label: string): string {
  return `field-${label.replace(/\W+/g, '-').toLowerCase()}`;
}

/** Every dead end a visitor can hit, written before anyone hits one. */
const EMPTY_COPY = {
  unknownSlug: "This booking page doesn't exist. Check the link you were given.",
  calendarDown: "Todd's calendar isn't answering right now. Give it a minute and refresh.",
  noSlots: `Nothing free in the next fortnight. Email ${CONTACT_EMAIL} and we'll find a time.`,
} as const;

export { DEFAULT_SLUG };

export default function BookPage() {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam ?? DEFAULT_SLUG;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const campaign = useMemo(() => {
    let storage: Storage | undefined;
    try { storage = window.localStorage; } catch { /* Storage may be unavailable. */ }
    const consent = hasMeasurementConsent(document.cookie, navigator);
    return resolveCampaignParameters(params.toString(), storage, consent);
  }, [params]);
  const partnership = isPartnershipBooking('/book/' + slug + '/', params.toString(), campaign);
  const capturedView = useRef(false);
  const capturedBooking = useRef(false);
  const trackPartnership = useCallback((event: string) => {
    if (!partnership) return;
    const properties: Record<string, string> = { campaign: 'ai-implementation-partnership', page_path: '/book/intro-30/' };
    Object.assign(properties, campaign);
    try {
      (window as Window & { __ix_analytics?: { capture: (event: string, props: Record<string, string>) => void } }).__ix_analytics?.capture(event, properties);
    } catch { /* Analytics must never interrupt a booking. */ }
  }, [partnership, campaign]);
  useEffect(() => {
    if (!partnership || capturedView.current) return;
    capturedView.current = true;
    trackPartnership('partnership_booking_viewed');
  }, [partnership, trackPartnership]);
  const [revenue, setRevenue] = useState('');
  const [heardFrom, setHeardFrom] = useState('');
  const from = useMemo(() => brisbaneToday(), []);

  // The visitor's real zone, read once. This is what gets SENT with the booking,
  // whichever clock they choose to read the page in.
  const visitorZone = useMemo(() => detectTimezone(), []);
  const [zone, setZone] = useState(visitorZone);

  const typeQuery = useBookingType(slug);
  const availability = useAvailability(slug, from);
  const refetchAvailability = availability.refetch;
  const onSlotConflict = useCallback(() => refetchAvailability(), [refetchAvailability]);
  const bookMutation = useBook(slug, onSlotConflict);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', notes: '', website: '' });
  // Set when the time somebody had chosen is gone from a refetched grid, and when a
  // submit is refused before it reaches the network. Both used to happen in silence.
  const [lostSlot, setLostSlot] = useState(false);
  const [formError, setFormError] = useState<{ message: string; focus?: string } | null>(null);
  const [apiErrorRead, setApiErrorRead] = useState(false);
  // When the times on screen were last issued, so a returning visitor is not refetched at.
  const lastLoadedRef = useRef(Date.now());

  const days = availability.data?.days;
  // Regrouped by the DISPLAYED zone. The API groups by Brisbane's date, and once the
  // times move so do the day boundaries.
  const daysWithSlots = useMemo(() => groupByLocalDay(days, zone), [days, zone]);

  // Land on the first day that actually has something free, so the common case is zero
  // taps before a time is visible. Re-runs when the zone changes, because the day the
  // selection pointed at may not exist under the new grouping.
  useEffect(() => {
    if (!daysWithSlots.length) return;
    if (!selectedDate || !daysWithSlots.some((d) => d.date === selectedDate)) {
      setSelectedDate(daysWithSlots[0].date);
    }
  }, [daysWithSlots, selectedDate]);

  // A slot the visitor picked can vanish under them when the grid refetches after a 409.
  //
  // Matched on the INSTANT, not on the token. A slot token carries an `iat` and lives 15
  // minutes, so every availability call mints a different token for the same time. Matching
  // on the token meant any refetch decided the visitor's 1:30pm no longer existed, dropped
  // the selection and greyed the button out with nothing said. The same 15 minutes is why
  // the fresh token is copied over the held one: a page left open past the expiry was
  // submitting a token the API had already stopped honouring.
  useEffect(() => {
    if (!selectedSlot || !days) return;
    const live = days.flatMap((d) => d.slots).find((s) => s.starts_at === selectedSlot.starts_at);
    if (!live) {
      setSelectedSlot(null);
      setLostSlot(true);
      return;
    }
    if (live.token !== selectedSlot.token) setSelectedSlot(live);
  }, [days, selectedSlot]);

  /**
   * Refresh the times when somebody comes back to a tab they left open.
   *
   * Slot tokens live 15 minutes. Without this, a page opened before lunch still shows the
   * morning's times and holds a token the API stopped honouring, so the first press of Book
   * is refused with "that time was taken a moment ago" when nothing of the sort happened.
   * Coming back to the tab is the moment to fix that, and it is free: the grid is already
   * kept on screen while a refetch is in flight.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedRef.current < STALE_AFTER_MS) return;
      lastLoadedRef.current = Date.now();
      refetchAvailability();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetchAvailability]);

  // A booking now has a page of its own, so the confirmation is a URL the visitor can
  // come back to rather than a screen that dies with the tab. `replace` so the back
  // button does not land them on a form for a booking they have already made.
  useEffect(() => {
    const booked = bookMutation.data;
    if (bookMutation.isSuccess && booked) {
      if (capturedBooking.current) return;
      if (!capturedBooking.current) {
        capturedBooking.current = true;
        trackPartnership('partnership_booking_completed');
        if (partnership) {
          void (async () => {
            try {
              const complete = (window as Window & { __ix_complete_ad_booking?: (path: string, bookingId: string) => boolean | Promise<boolean> }).__ix_complete_ad_booking;
              if (await complete?.('/book' + refPathOf(booked.reference_url), booked.booking_id)) return;
            } catch { /* Measurement must never block confirmation. */ }
            navigate(refPathOf(booked.reference_url), { replace: true });
          })();
          return;
        }
      }
      navigate(refPathOf(booked.reference_url), { replace: true });
    }
  }, [bookMutation.isSuccess, bookMutation.data, navigate, trackPartnership, partnership]);

  if (typeQuery.isLoading) {
    return <Shell><p style={{ color: MUTED }}>Loading the times…</p></Shell>;
  }

  if (typeQuery.isError) {
    return (
      <Shell>
        <Notice
          heading="We can't find that page"
          message={typeQuery.error?.message ?? EMPTY_COPY.unknownSlug}
          action={<a href={`/book/${DEFAULT_SLUG}/`} style={{ color: NAVY, fontWeight: 600 }}>Book an intro call instead</a>}
        />
      </Shell>
    );
  }

  const type = typeQuery.data!;
  const activeDay = daysWithSlots.find((d) => d.date === selectedDate) ?? null;

  /**
   * Retract the complaint the moment the visitor acts on it, rather than on the next submit.
   *
   * The mutation's own error cannot be reset from here, so it is marked read instead. Without
   * that, "that time was taken a moment ago" would sit under the button after the visitor had
   * already done what it asked and picked another time.
   */
  function clearMessages() {
    setFormError((current) => (current === null ? current : null));
    setLostSlot((current) => (current === false ? current : false));
    setApiErrorRead((current) => (current === true ? current : true));
  }

  /**
   * What is stopping this booking, in the order somebody fills the page in.
   *
   * The button used to be disabled until all three passed, which is why pressing Book
   * could do nothing at all and say nothing about why. Nobody is told that a button is
   * disabled; they are told it is broken. The button now always submits and this decides
   * what the visitor reads.
   */
  function firstProblem(): { message: string; focus?: string } | null {
    if (!selectedSlot) return { message: 'Pick a time first. Choose a day, then one of the times beside it.' };
    if (!form.name.trim()) return { message: 'We need a name to put on the invite.', focus: fieldId('Name') };
    if (!EMAIL_RE.test(form.email.trim())) {
      return {
        message: form.email.trim()
          ? `That email address doesn't look right. Check ${form.email.trim()} and try again.`
          : 'We need an email address to send the invite to.',
        focus: fieldId('Email'),
      };
    }
    if (partnership && !form.company.trim()) return { message: 'Please enter your company so we can prepare for the call.', focus: fieldId('Company') };
    return null;
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (bookMutation.isPending) return;

    const problem = firstProblem();
    if (problem || !selectedSlot) {
      setFormError(problem ?? null);
      if (problem?.focus) {
        document.getElementById(problem.focus)?.focus();
      } else {
        // The message sits by the button and the times are a column away on desktop, so
        // the grid is brought to the visitor rather than left for them to go and find.
        const grid = document.querySelector('.booking-layout__calendar');
        if (grid instanceof HTMLElement && typeof grid.scrollIntoView === 'function') {
          grid.scrollIntoView({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }
      return;
    }

    setFormError(null);
    setLostSlot(false);
    setApiErrorRead(false);
    bookMutation.mutate({
      token: selectedSlot.token,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      company: form.company.trim() || undefined,
      // The answer rides in the notes FRIDAY already stores, so the CRM sees it without an API change.
      notes: [partnership ? 'AI Implementation Partnership strategy call' : '', partnership && revenue ? `Annual revenue (AUD): ${revenue}` : '', heardFrom ? `Heard about InnovateX: ${heardFrom}` : '', form.notes.trim()].filter(Boolean).join('\n\n') || undefined,
      website: form.website,
      // Their real zone, not the one they are reading in. Somebody in Perth who flicked
      // the page to Brisbane time is still in Perth when the reminder arrives.
      timezone: visitorZone,
      source: params.get('source') ?? (partnership ? 'ai-implementation-partnership' : undefined),
      ...campaign,
    });
  }

  // The FRIDAY original raised this as a toast. There is no toast host on the marketing
  // site, and a message pinned above the button it belongs to is the better place anyway.
  //
  // Three sources, one line, and the API outranks the page. When a submit came back with
  // "that time was taken a moment ago", the refetch it triggers ALSO removes the slot and
  // trips `lostSlot`, and of the two sentences the API's is the one written for what
  // actually happened. `lostSlot` is left to cover the case the API never sees, where a
  // time disappears from a refetch the visitor did not cause. Nothing here fails quietly.
  const apiError = bookMutation.isError && !apiErrorRead
    ? (bookMutation.error instanceof ApiError
      ? bookMutation.error.message
      : 'Something went wrong at our end. Give it a moment and try again.')
    : null;
  const bookError = formError?.message
    ?? apiError
    ?? (lostSlot ? 'That time went while you were filling this in. The times are up to date now, so pick another one.' : null);
  // "You already have one of these booked" arrives with the booking it is about, so the
  // refusal can be a way through rather than a wall.
  const heldBookingUrl = apiError && bookMutation.error instanceof ApiError
    ? bookMutation.error.bookingUrl
    : null;

  const showZoneChoice = !readsAsBrisbane(
    daysWithSlots[0]?.labelAt ?? Math.floor(Date.now() / 1000), visitorZone,
  );

  return (
    <Shell title={type.title} photoUrl={type.host_photo_url}>
      {partnership && <div className="partnership-call-context"><h2>AI implementation strategy call</h2><p>For businesses with A$10–200 million annual revenue. Discuss your AI priorities, team training and whether an ongoing leadership partnership fits.</p><p>Your calendar invitation will be titled “Intro call with Todd”.</p></div>}
      <p style={{ margin: '-8px 0 6px', fontSize: 15, color: MUTED }}>
        {`${type.duration_mins} minutes`}
      </p>
      {showZoneChoice
        ? <TimezoneChoice zone={zone} visitorZone={visitorZone} onChange={setZone} />
        : (
          <p style={{ margin: '0 0 20px', fontSize: 14, color: MUTED }}>
            Times shown in Brisbane time (AEST).
          </p>
        )}

      {availability.isLoading ? <p style={{ color: MUTED }}>Checking what's free…</p> : null}

      {availability.isError ? (
        <Notice
          heading="The calendar isn't answering"
          message={EMPTY_COPY.calendarDown}
          action={
            <PrimaryButton onClick={() => availability.refetch()} style={{ maxWidth: 220, margin: '0 auto' }}>
              Try again
            </PrimaryButton>
          }
        />
      ) : null}

      {availability.isSuccess && daysWithSlots.length === 0 ? (
        <Notice
          heading="Nothing free just now"
          message={EMPTY_COPY.noSlots}
          action={<a href={`mailto:${CONTACT_EMAIL}`} style={{ color: NAVY, fontWeight: 600 }}>Email us</a>}
        />
      ) : null}

      {availability.isSuccess && daysWithSlots.length > 0 ? (
        /* Mobile order is DOM order: blurb, calendar, form. The grid areas in styles.css
           put the blurb and the form in a left column above 768px and leave the calendar
           on the right, without moving anything in the markup. */
        <div className="booking-layout">
          <div className="booking-layout__context">
            {type.description ? (
              <p style={{ margin: '0 0 24px', fontSize: 16, lineHeight: 1.6 }}>{type.description}</p>
            ) : null}
          </div>

          <div className="booking-layout__calendar">
            <DayStrip
              days={daysWithSlots}
              selected={selectedDate}
              zone={zone}
              onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); clearMessages(); }}
            />
            <SlotGrid
              day={activeDay}
              selected={selectedSlot}
              zone={zone}
              onSelect={(s) => { setSelectedSlot(s); clearMessages(); }}
            />
          </div>

          {/* `noValidate` hands validation to `submit`. The browser's own bubbles cannot say
              anything about the time, which is not a form control, so leaving them on meant
              two different-looking refusals and one case neither of them covered. `required`
              stays for what it tells a screen reader. */}
          <form onSubmit={submit} noValidate className="booking-layout__form" style={{ marginTop: 28 }}>
            <SectionHeading>Your details</SectionHeading>

            <Field
              label="Name" required autoComplete="name" value={form.name}
              invalid={formError?.focus === fieldId('Name')}
              onChange={(v) => { setForm({ ...form, name: v }); clearMessages(); }}
            />
            <Field
              label="Email" required type="email" autoComplete="email" value={form.email}
              invalid={formError?.focus === fieldId('Email')}
              onChange={(v) => { setForm({ ...form, email: v }); clearMessages(); }}
            />
            <Field label="Phone (optional)" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} autoComplete="tel" />
            <Field label={partnership ? "Company" : "Company (optional)"} required={partnership} invalid={formError?.focus === fieldId("Company")} value={form.company} onChange={(v) => setForm({ ...form, company: v })} autoComplete="organization" />
            {partnership && <SelectField id="partnership-revenue" label="Annual revenue (AUD, optional)" value={revenue} onChange={setRevenue} placeholder="Prefer to discuss" options={['Under $10 million', '$10–20 million', '$20–50 million', '$50–100 million', '$100–200 million', 'Over $200 million']} hint="This helps us prepare for the conversation." />}
            <SelectField id="heard-from" label="How did you hear about InnovateX? (optional)" value={heardFrom} onChange={setHeardFrom} placeholder="Select an option" options={['Google or another search engine', 'AI assistant (ChatGPT, Claude, Copilot or Perplexity)', 'LinkedIn', 'Referral from someone', 'Event, talk or masterclass', 'Government panel or Local Buy', 'Other']} hint="This helps us understand how people find us." />
            <Field label="What would you like to cover? (optional)" multiline value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />

            {/* Honeypot. Hidden from people and from screen readers; bots fill it in. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />

            {bookError ? (
              <p role="alert" style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.6, color: CORAL }}>
                {bookError}
                {heldBookingUrl ? (
                  <>
                    {' '}
                    <a href={heldBookingUrl} style={{ color: NAVY, fontWeight: 600 }}>Open your booking</a>
                  </>
                ) : null}
              </p>
            ) : null}

            {/* Disabled only while the request is in flight, which is the one case where
                pressing it again would be wrong AND the label already says why. Every other
                refusal is made by `submit` so it can be read rather than guessed at. */}
            <PrimaryButton type="submit" disabled={bookMutation.isPending} style={{ marginTop: 8 }}>
              {bookMutation.isPending
                ? 'Booking…'
                : selectedSlot
                  ? `Book ${formatTime(selectedSlot.starts_at, zone)}`
                  : 'Pick a time first'}
            </PrimaryButton>
          </form>
        </div>
      ) : null}
    </Shell>
  );
}

/**
 * The router path inside `/book` for a reference URL the API built.
 *
 * The API returns an absolute URL because it also puts that string in emails. React
 * Router is mounted with a `/book` basename, so it wants the part after it, and falling
 * back to the whole string keeps a hand-set BOOKING_PAGE_BASE_URL from breaking the
 * redirect on a local build.
 */
export function refPathOf(referenceUrl: string): string {
  const match = /\/book(\/ref\/[^/?#]+)/.exec(referenceUrl);
  return match ? match[1] : referenceUrl;
}

function Field(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  multiline?: boolean;
  autoComplete?: string;
  /** Outlines the field the submit message is about, so the words and the box agree. */
  invalid?: boolean;
}) {
  const id = fieldId(props.label);
  const shared = {
    id,
    value: props.value,
    required: props.required,
    'aria-invalid': props.invalid || undefined,
    autoComplete: props.autoComplete,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => props.onChange(e.target.value),
    style: {
      width: '100%', minHeight: 48, boxSizing: 'border-box' as const, padding: '12px 14px',
      border: `1px solid ${props.invalid ? CORAL : LINE}`, borderRadius: 14, fontSize: 16,
      fontFamily: 'inherit', color: INK, background: '#FFFFFF',
    },
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={{ display: 'block', marginBottom: 6, fontSize: 14, color: MUTED }}>
        {props.label}
      </label>
      {props.multiline
        ? <textarea {...shared} rows={3} style={{ ...shared.style, minHeight: 88, resize: 'vertical' }} />
        : <input {...shared} type={props.type ?? 'text'} />}
    </div>
  );
}
